/** Client-side fee ESTIMATES only — never authoritative. Server decides 5% vs 2% at settlement. */

export const FEE_RATE_FREE = 0.05;
export const FEE_RATE_PLUS = 0.02;
export const PLUS_TIER_ID = 'lockedin_plus';

/** Shown on pre-settlement UI where fee is estimated, not final. */
export const FEE_ESTIMATE_DISCLAIMER =
  'Final transfer amount is calculated at settlement based on the winner\'s subscription status.';

/**
 * Canonical client definition of "subscription is active". The single source of
 * truth for Plus gating on the client — every other helper delegates here.
 */
export const ACTIVE_SUBSCRIPTION_STATUSES = new Set(['active', 'trialing']);

/** Canonical "is this user Plus?" predicate (active or trialing). */
export function hasLockedInPlus(profile) {
  if (!profile) return false;
  return ACTIVE_SUBSCRIPTION_STATUSES.has(profile.subscriptionStatus);
}

/** Raw-status variant (for places that only have the status string, e.g. checkout polling). */
export function isActiveSubscriptionStatus(status) {
  return ACTIVE_SUBSCRIPTION_STATUSES.has(status);
}

/** @deprecated use hasLockedInPlus */
export function hasPlusFeeRate(profile) {
  return hasLockedInPlus(profile);
}

/** UI estimate only — not used for settlement. */
export function getEstimatedFeeRate(profileOrHasPlus) {
  const hasPlus = typeof profileOrHasPlus === 'boolean'
    ? profileOrHasPlus
    : hasLockedInPlus(profileOrHasPlus);
  return hasPlus ? FEE_RATE_PLUS : FEE_RATE_FREE;
}

/** @deprecated use getEstimatedFeeRate — client must not treat this as settlement truth */
export function getPlatformFeeRate(profileOrHasPlus) {
  return getEstimatedFeeRate(profileOrHasPlus);
}

export function dollarsToCents(dollars) {
  return Math.round(Number(dollars || 0) * 100);
}

export function centsToDollars(cents) {
  return Math.round(Number(cents)) / 100;
}

/**
 * Pre-settlement UI estimate — if you win, assuming viewer/winner subscription.
 * Server recalculates from winner Firestore doc at settlement.
 */
export function estimateSettlement(totalPotDollars, profileOrHasPlus) {
  const rate = getEstimatedFeeRate(profileOrHasPlus);
  const totalPotCents = dollarsToCents(totalPotDollars);
  const feeCents = Math.round(totalPotCents * rate);
  const payoutCents = totalPotCents - feeCents;
  const hasPlus = rate === FEE_RATE_PLUS;
  return {
    totalPot: centsToDollars(totalPotCents),
    platformFeeRate: rate,
    platformFeeAmount: centsToDollars(feeCents),
    platformFee: centsToDollars(feeCents),
    winnerPayoutAmount: centsToDollars(payoutCents),
    payoutAmount: centsToDollars(payoutCents),
    subscriptionDiscountApplied: hasPlus,
    _estimate: true,
  };
}

/** @deprecated use estimateSettlement */
export function calculateSettlement(totalPotDollars, profileOrHasPlus) {
  return estimateSettlement(totalPotDollars, profileOrHasPlus);
}

export function isServerSettlement(settlement) {
  if (!settlement || settlement._estimate) return false;
  return settlement.settledAt != null;
}

export function formatFeeRateLabel(rate) {
  const pct = Math.round(rate * 100);
  return rate === FEE_RATE_PLUS ? `LockedIn Plus ${pct}%` : `Platform ${pct}%`;
}

export function formatPercent(rate) {
  if (rate == null || Number.isNaN(Number(rate))) return '—';
  return `${Math.round(Number(rate) * 100)}%`;
}

/** Display-only — never used for settlement math. */
export function formatPayoutDollars(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `$${Number(value).toFixed(2)}`;
}

/**
 * Merge server/partial settlement with UI estimate so display never crashes on missing fields.
 * Does not compute authoritative fees — estimate fills gaps for preview only.
 */
export function resolvePayoutBreakdown({ settlement, totalPot, subscriptionProfile } = {}) {
  const pot = totalPot ?? settlement?.totalPot;
  const hasPot = pot != null && !Number.isNaN(Number(pot));
  const estimate = hasPot ? estimateSettlement(pot, subscriptionProfile) : null;

  if (!settlement) {
    return estimate ?? {
      totalPot: null,
      platformFeeRate: null,
      platformFeeAmount: null,
      winnerPayoutAmount: null,
      subscriptionDiscountApplied: false,
    };
  }

  return {
    totalPot: settlement.totalPot ?? estimate?.totalPot ?? null,
    platformFeeRate: settlement.platformFeeRate ?? estimate?.platformFeeRate ?? null,
    platformFeeAmount:
      settlement.platformFeeAmount
      ?? settlement.platformFee
      ?? estimate?.platformFeeAmount
      ?? null,
    winnerPayoutAmount:
      settlement.winnerPayoutAmount
      ?? settlement.payoutAmount
      ?? estimate?.winnerPayoutAmount
      ?? null,
    subscriptionDiscountApplied:
      settlement.subscriptionDiscountApplied ?? estimate?.subscriptionDiscountApplied ?? false,
    settledAt: settlement.settledAt,
    _estimate: settlement._estimate ?? estimate?._estimate,
  };
}
